import { useEffect, useState } from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '../auth/AuthProvider'
import { portalGetTenantOverview } from '../lib/portalApi'

type Split = { ios: number; android: number; all: number }
type Metrics = { mrr: number; active: Split; new: Split; churn: Split }
type Bucket = { label: string; metrics: Metrics }
type AppRow = { appId: string; name: string; deleted?: boolean; metrics: Metrics }

const money = (v: number) =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`

/** Tỷ lệ rời bỏ = rời đi / (rời đi + còn lại). */
function churnRate(m?: Metrics) {
  const churn = m?.churn?.all ?? 0
  const base = churn + (m?.active?.all ?? 0)
  return base > 0 ? (churn / base) * 100 : 0
}

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
      <h3 className="m-0 font-label-md text-[14px] text-on-surface-variant">{label}</h3>
      <div className={`font-headline-lg text-[26px] md:text-[36px] font-bold mt-2 ${tone ?? 'text-on-surface'}`}>{value}</div>
      {hint && <div className="font-label-sm text-[12px] mt-2 text-on-surface-variant">{hint}</div>}
    </div>
  )
}

export function AnalyticsPage() {
  const { user } = useAuth()
  const [period, setPeriod] = useState<'month' | 'day'>('month')
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!user) return
      setLoading(true)
      setError(null)
      try {
        const idToken = await user.getIdToken()
        const res = await portalGetTenantOverview({ tenantId: user.uid, idToken, period })
        if (!cancelled) setData(res)
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Không tải được số liệu')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [user, period])

  if (loading) return <div className="p-xl text-on-surface">Đang tải số liệu…</div>
  if (error)
    return (
      <div className="p-md bg-error-container text-on-error-container rounded-lg border border-error/20 font-body-sm">
        {error}
      </div>
    )

  const history: Bucket[] = data?.history ?? []
  const current: Metrics | undefined = data?.metrics
  const prev = history.length > 1 ? history[history.length - 2].metrics : undefined
  const byApp: AppRow[] = data?.byApp ?? []

  const growth = (now: number, before?: number) => {
    if (before === undefined || before === 0) return now > 0 ? '+100%' : 'không đổi'
    const g = ((now - before) / before) * 100
    return `${g >= 0 ? '+' : ''}${g.toFixed(1)}% so với kỳ trước`
  }

  const chartData = history.map((h) => ({
    name: h.label,
    mrr: h.metrics?.mrr ?? 0,
    active: h.metrics?.active?.all ?? 0,
    iOS: h.metrics?.active?.ios ?? 0,
    Android: h.metrics?.active?.android ?? 0,
    new: h.metrics?.new?.all ?? 0,
    churn: h.metrics?.churn?.all ?? 0,
  }))

  const tooltipStyle = {
    backgroundColor: '#191c1e',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '12px',
  }

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex flex-wrap items-center justify-between gap-md">
        <div>
          <h1 className="font-headline-lg text-[22px] md:text-[28px] font-semibold text-on-surface">Analytics</h1>
          <p className="font-body-sm text-[13px] text-on-surface-variant mt-1">
            Toàn bộ app trong tài khoản, đã loại giao dịch sandbox.
          </p>
        </div>
        <div className="flex gap-1 bg-surface-container-low rounded-lg p-1">
          {(['month', 'day'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-md py-1.5 rounded-md font-label-md text-[13px] transition-colors ${
                period === p ? 'bg-surface-container-lowest text-on-surface shadow-sm' : 'text-on-surface-variant'
              }`}
            >
              {p === 'month' ? 'Theo tháng' : 'Theo ngày'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-md md:gap-lg">
        <Kpi
          label="MRR"
          value={money(current?.mrr ?? 0)}
          hint={growth(current?.mrr ?? 0, prev?.mrr)}
          tone="text-[#15803d]"
        />
        <Kpi
          label="Đang đăng ký"
          value={(current?.active?.all ?? 0).toLocaleString()}
          hint={`iOS ${current?.active?.ios ?? 0} · Android ${current?.active?.android ?? 0}`}
          tone="text-primary"
        />
        <Kpi
          label="Người mới"
          value={(current?.new?.all ?? 0).toLocaleString()}
          hint={growth(current?.new?.all ?? 0, prev?.new?.all)}
        />
        <Kpi
          label="Tỷ lệ rời bỏ"
          value={`${churnRate(current).toFixed(1)}%`}
          hint={`${current?.churn?.all ?? 0} rời đi trong kỳ`}
          tone={churnRate(current) > 0 ? 'text-error' : undefined}
        />
      </div>

      <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
        <h2 className="font-headline-md text-[18px] font-semibold text-on-surface mb-lg">MRR theo thời gian</h2>
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="aMrr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#15803d" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#15803d" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" stroke="#737686" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#737686" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e3e5" />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="mrr" stroke="#15803d" strokeWidth={3} fill="url(#aMrr)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-lg">
        <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
          <h2 className="font-headline-md text-[18px] font-semibold text-on-surface mb-lg">
            Đang đăng ký theo nền tảng
          </h2>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" stroke="#737686" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#737686" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e3e5" />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#f2f4f6' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="iOS" stackId="p" fill="#004ac6" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Android" stackId="p" fill="#15803d" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
          <h2 className="font-headline-md text-[18px] font-semibold text-on-surface mb-lg">Người mới và người rời bỏ</h2>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" stroke="#737686" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#737686" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e3e5" />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#f2f4f6' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="new" name="Người mới" fill="#004ac6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="churn" name="Rời bỏ" fill="#b3261e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-surface-container-lowest border border-outline-variant rounded-xl shadow-sm overflow-hidden">
        <h2 className="font-headline-md text-[18px] font-semibold text-on-surface p-lg pb-md">So sánh theo app</h2>
        {byApp.length === 0 ? (
          <p className="px-lg pb-lg font-body-sm text-[13px] text-on-surface-variant">
            Kỳ này chưa có sự kiện đăng ký nào.
          </p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-y border-outline-variant bg-surface-container-low">
                <th className="text-left px-lg py-sm font-label-sm text-[12px] text-on-surface-variant">App</th>
                <th className="text-right px-lg py-sm font-label-sm text-[12px] text-on-surface-variant">MRR</th>
                <th className="text-right px-lg py-sm font-label-sm text-[12px] text-on-surface-variant">Đang đăng ký</th>
                <th className="text-right px-lg py-sm font-label-sm text-[12px] text-on-surface-variant">Mới</th>
                <th className="text-right px-lg py-sm font-label-sm text-[12px] text-on-surface-variant">Rời bỏ</th>
              </tr>
            </thead>
            <tbody>
              {byApp.map((a) => (
                <tr key={a.appId} className="border-b border-outline-variant/50">
                  <td className="px-lg py-md font-body-sm text-[13px] text-on-surface">
                    {a.name}
                    {a.deleted && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full bg-surface-container-high text-on-surface-variant text-[11px]">
                        đã xoá
                      </span>
                    )}
                    <div className="font-mono text-[11px] text-on-surface-variant">{a.appId}</div>
                  </td>
                  <td className="px-lg py-md text-right font-body-sm text-[13px] text-[#15803d] font-semibold">
                    {money(a.metrics.mrr)}
                  </td>
                  <td className="px-lg py-md text-right font-body-sm text-[13px] text-on-surface">
                    {a.metrics.active.all}
                    <span className="text-on-surface-variant text-[11px] ml-1">
                      ({a.metrics.active.ios}/{a.metrics.active.android})
                    </span>
                  </td>
                  <td className="px-lg py-md text-right font-body-sm text-[13px] text-on-surface">{a.metrics.new.all}</td>
                  <td className="px-lg py-md text-right font-body-sm text-[13px] text-on-surface">{a.metrics.churn.all}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        <p className="px-lg py-md font-body-sm text-[11px] text-on-surface-variant border-t border-outline-variant">
          Cột "Đang đăng ký" ghi kèm (iOS/Android). MRR chỉ cộng những giao dịch có giá thật từ store —
          giao dịch thiếu giá không được đoán để tránh thổi phồng con số.
        </p>
      </div>
    </div>
  )
}
